import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Zap, Save, ArrowRight, Loader2 } from "lucide-react";

import { API } from "@/lib/api";
import { readToken } from "@/lib/tokenStore";
import {
  KIND_OPTIONS,
  SOURCE_TYPE_OPTIONS,
  PRIORITY_OPTIONS,
} from "@/pages/journey/leadOptions";
import {
  Field,
  FormSection,
  inputClass,
  textareaClass,
} from "@/components/journey/LeadFormPrimitives";

const EMPTY = {
  kind: "standort",
  source_type: "phone",
  source_label: "",
  name: "",
  company: "",
  email: "",
  phone: "",
  city_region: "",
  note_text: "",
  next_step: "",
  follow_up_at: "",
  priority: "normal",
};

export default function QuickAdd() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const submit = async (after) => {
    const token = readToken();
    if (!token) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      );
      const r = await axios.post(`${API}/admin/crm/leads`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 20000,
      });
      const id = r.data?.lead?.id;
      toast.success("Lead erfasst");
      if (after === "detail" && id) {
        navigate(`/admin/journey/leads/${id}`);
      } else if (after === "another") {
        setForm(EMPTY);
      } else {
        navigate("/admin/journey/inbox");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let message;
      if (Array.isArray(detail)) {
        message = detail.map((d) => d.msg).join(" · ");
      } else if (typeof detail === "string") {
        message = detail;
      } else {
        message = err?.message || "Unbekannter Fehler";
      }
      setError(message);
      toast.error("Konnte Lead nicht speichern");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="journey-quick-add" className="mx-auto max-w-xl space-y-6">
      <div className="flex items-center justify-between">
        <Link
          to="/admin/journey/inbox"
          className="inline-flex items-center gap-1.5 text-xs text-zinc-400 transition hover:text-zinc-100"
          data-testid="quick-add-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Zurück zur Inbox
        </Link>
        <Link
          to="/admin/journey/leads/new"
          className="hidden text-xs text-zinc-400 transition hover:text-zinc-100 sm:inline"
          data-testid="quick-add-switch-full"
        >
          Vollständig erfassen →
        </Link>
      </div>

      <header className="space-y-1">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-emerald-400/80">
          <Zap className="h-3.5 w-3.5" />
          Quick Add
        </div>
        <h1 className="text-xl font-semibold text-zinc-100">Lead schnell erfassen</h1>
        <p className="text-sm text-zinc-400">
          Für Telefonate, Messen, LinkedIn-Kontakte oder spontane Gespräche.
        </p>
      </header>

      {error ? (
        <div
          data-testid="quick-add-error"
          className="rounded-md border border-red-500/30 bg-red-500/[0.05] p-3 text-xs text-red-200"
        >
          {error}
        </div>
      ) : null}

      <FormSection title="Lead-Einordnung" testId="quick-add-section-classification">
        <Field label="Leadtyp" required testId="quick-add-kind">
          <select
            value={form.kind}
            onChange={update("kind")}
            data-testid="quick-add-kind-select"
            className={inputClass}
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Quelle" required testId="quick-add-source">
          <select
            value={form.source_type}
            onChange={update("source_type")}
            data-testid="quick-add-source-select"
            className={inputClass}
          >
            {SOURCE_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Quelle Zusatz (optional)" hint="z.B. Messename, LinkedIn-Profil, Empfehlung durch …">
          <input
            type="text"
            value={form.source_label}
            onChange={update("source_label")}
            data-testid="quick-add-source-label"
            className={inputClass}
          />
        </Field>
      </FormSection>

      <FormSection
        title="Kontakt"
        subtitle="Mindestens eines: Name, E-Mail, Telefon oder Firma."
        testId="quick-add-section-contact"
      >
        <Field label="Name">
          <input
            type="text"
            value={form.name}
            onChange={update("name")}
            placeholder="Vorname Nachname"
            data-testid="quick-add-name"
            className={inputClass}
            autoFocus
          />
        </Field>
        <Field label="Firma">
          <input
            type="text"
            value={form.company}
            onChange={update("company")}
            data-testid="quick-add-company"
            className={inputClass}
          />
        </Field>
        <Field label="E-Mail">
          <input
            type="email"
            value={form.email}
            onChange={update("email")}
            data-testid="quick-add-email"
            className={inputClass}
          />
        </Field>
        <Field label="Telefon">
          <input
            type="tel"
            value={form.phone}
            onChange={update("phone")}
            data-testid="quick-add-phone"
            className={inputClass}
          />
        </Field>
        <Field label="Region / Ort">
          <input
            type="text"
            value={form.city_region}
            onChange={update("city_region")}
            data-testid="quick-add-region"
            className={inputClass}
          />
        </Field>
      </FormSection>

      <FormSection
        title="Kontext"
        subtitle="Mindestens eines: Gesprächsnotiz oder nächster Schritt."
        testId="quick-add-section-context"
      >
        <Field label="Gesprächsnotiz">
          <textarea
            value={form.note_text}
            onChange={update("note_text")}
            data-testid="quick-add-note"
            placeholder="Worum ging es? Was war wichtig?"
            className={textareaClass}
          />
        </Field>
        <Field label="Nächster Schritt">
          <input
            type="text"
            value={form.next_step}
            onChange={update("next_step")}
            placeholder="z.B. Mailen, Anrufen, Profil prüfen"
            data-testid="quick-add-next-step"
            className={inputClass}
          />
        </Field>
        <Field label="Follow-up Datum / Uhrzeit (optional)">
          <input
            type="datetime-local"
            value={form.follow_up_at}
            onChange={update("follow_up_at")}
            data-testid="quick-add-follow-up-at"
            className={inputClass}
          />
        </Field>
        <Field label="Priorität">
          <select
            value={form.priority}
            onChange={update("priority")}
            data-testid="quick-add-priority"
            className={inputClass}
          >
            {PRIORITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>
      </FormSection>

      <div className="sticky bottom-0 -mx-4 flex flex-col gap-2 border-t border-white/[0.06] bg-[#050607]/95 p-4 backdrop-blur sm:mx-0 sm:flex-row sm:items-center sm:justify-end sm:rounded-md sm:border">
        <Link
          to="/admin/journey/inbox"
          className="inline-flex items-center justify-center rounded border border-white/10 px-4 py-2 text-xs text-zinc-400 transition hover:border-white/20 hover:text-zinc-100"
          data-testid="quick-add-cancel"
        >
          Abbrechen
        </Link>
        <button
          type="button"
          disabled={submitting}
          onClick={() => submit("inbox")}
          data-testid="quick-add-save"
          className="inline-flex items-center justify-center gap-1.5 rounded border border-white/10 px-4 py-2 text-xs text-zinc-200 transition hover:border-white/20 hover:bg-white/5 disabled:opacity-40"
        >
          {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Lead speichern
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={() => submit("detail")}
          data-testid="quick-add-save-detail"
          className="inline-flex items-center justify-center gap-1.5 rounded bg-emerald-500 px-4 py-2 text-xs font-medium text-zinc-950 transition hover:bg-emerald-400 disabled:opacity-50"
        >
          Speichern & öffnen
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
